import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Download, Loader2, ExternalLink, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// CJ warehouses in the EU (the backend sends the same list; this is the fallback)
const EU_WAREHOUSES = ['DE', 'PL', 'CZ', 'FR', 'ES', 'IT', 'NL', 'BE', 'HU'];

const PRESETS = [
  'vibrator', 'lingerie', 'lubricant', 'sex toy', 'massager',
  'bondage', 'couples toy', 'wellness intimate', 'rose toy', 'dildo'
];

export const AdminCJImport = () => {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [imported, setImported] = useState({});
  const [importing, setImporting] = useState({});
  const [bulkImporting, setBulkImporting] = useState(false);
  const [markup, setMarkup] = useState(8.5);
  const [pidInput, setPidInput] = useState('');
  const [pidImporting, setPidImporting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryOverride, setCategoryOverride] = useState('');
  // Browse by CJ category / EU warehouse (Product List V2)
  const [cjCategories, setCjCategories] = useState([]);
  const [warehouses, setWarehouses] = useState(EU_WAREHOUSES);
  const [cjCategoriesError, setCjCategoriesError] = useState('');
  const [cjCategory, setCjCategory] = useState('');
  const [warehouse, setWarehouse] = useState('DE');
  const [adultOnly, setAdultOnly] = useState(true);
  const [browsing, setBrowsing] = useState(false); // last results came from browse

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const res = await axios.get(`${API}/admin/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCategories(res.data || []);
      } catch (e) {
        console.error('Failed to load categories', e);
      }
    };
    loadCategories();
    axios
      .get(`${API}/admin/cj/categories`, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } })
      .then((res) => {
        setCjCategories(res.data.categories || []);
        if (res.data.warehouses?.length) setWarehouses(res.data.warehouses);
      })
      .catch((e) => {
        const code = e?.response?.status;
        setCjCategoriesError(
          code === 404
            ? 'CJ categories are not available: the backend is older than this admin page — redeploy the backend (wrangler deploy).'
            : e?.response?.data?.detail || 'Could not load CJ categories'
        );
      });
  }, []);

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`
  });

  // Category or warehouse picked -> Product List V2; plain keyword -> classic search
  const search = async (q = keyword, p = 1) => {
    const browse = Boolean(cjCategory || warehouse);
    if (!q.trim() && !cjCategory) {
      toast.error(browse ? 'Pick a CJ category or enter a keyword' : 'Enter a keyword');
      return;
    }
    setLoading(true);
    try {
      const res = browse
        ? await axios.get(`${API}/admin/cj/browse`, {
            headers: authHeader(),
            params: { keyword: q, category_id: cjCategory, country: warehouse, page: p, page_size: 24 },
          })
        : await axios.get(`${API}/admin/cj/search`, {
            headers: authHeader(),
            params: { keyword: q, page: p, page_size: 24 },
          });
      setBrowsing(browse);
      if (browse) {
        const done = {};
        (res.data.items || []).forEach((it) => { if (it.imported) done[it.pid] = true; });
        setImported((s) => ({ ...s, ...done }));
      }
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(p);
      setKeyword(q);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'CJ search failed');
    } finally {
      setLoading(false);
    }
  };

  const importItem = async (pid) => {
    setImporting((s) => ({ ...s, [pid]: true }));
    try {
      const body = { pid, markup_eur: Number(markup) || 0 };
      if (categoryOverride) body.category_id = categoryOverride;
      if (browsing && warehouse) body.from_country = warehouse;
      const res = await axios.post(
        `${API}/admin/cj/import`,
        body,
        { headers: authHeader() }
      );
      setImported((s) => ({ ...s, [pid]: true }));
      if (res.data.duplicate) {
        toast.info('Already in catalog');
      } else {
        toast.success(`Imported as €${res.data.price} → ${res.data.category || 'uncategorized'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting((s) => ({ ...s, [pid]: false }));
    }
  };

  const importByPid = async () => {
    const raw = pidInput.trim();
    if (!raw) {
      toast.error('Enter a CJ product ID or URL');
      return;
    }
    // Extract pid from a CJ URL if user pasted one
    // e.g. https://www.cjdropshipping.com/product/-p-2046070873585651714.html
    const match = raw.match(/(\d{15,25})/);
    const pid = match ? match[1] : raw;

    setPidImporting(true);
    try {
      const body = { pid, markup_eur: Number(markup) || 0 };
      if (categoryOverride) body.category_id = categoryOverride;
      const res = await axios.post(
        `${API}/admin/cj/import`,
        body,
        { headers: authHeader() }
      );
      if (res.data.duplicate) {
        toast.info(`Already in catalog: ${res.data.name || pid}`);
      } else {
        toast.success(`Imported: ${res.data.name} → €${res.data.price}`);
      }
      setPidInput('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setPidImporting(false);
    }
  };

  const bulkImport = async () => {
    if (!keyword.trim() && !(browsing && cjCategory)) {
      toast.error('Run a search first');
      return;
    }
    if (!window.confirm(`Import all ${items.length} results on this page?`)) return;
    setBulkImporting(true);
    try {
      const body = {
        keyword,
        markup_eur: Number(markup) || 0,
        page,
        page_size: items.length,
      };
      if (categoryOverride) body.category_id = categoryOverride;
      if (browsing) {
        if (cjCategory) body.cj_category_id = cjCategory;
        if (warehouse) body.from_country = warehouse;
      }
      const res = await axios.post(
        `${API}/admin/cj/bulk-import`,
        body,
        { headers: authHeader() }
      );
      const { imported: imp, skipped, failed } = res.data;
      toast.success(`Imported: ${imp} | Already in catalog: ${skipped} | Failed: ${failed}`);
      // Mark all visible as imported
      const newImported = {};
      items.forEach((it) => { newImported[it.pid] = true; });
      setImported((s) => ({ ...s, ...newImported }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk import failed');
    } finally {
      setBulkImporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / 24));

  return (
    <AdminLayout>
      <div className="p-8 space-y-6" data-testid="cj-import-page">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">CJ Dropshipping Import</h1>
          <p className="text-muted-foreground mt-1">
            Search the global CJ catalog and import products directly into your store.
          </p>
        </div>

        {/* Search bar */}
        <div className="bg-card border border-border rounded-xl p-5 space-y-4">
          <form
            onSubmit={(e) => { e.preventDefault(); search(keyword, 1); }}
            className="flex flex-col sm:flex-row gap-3"
          >
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="cj-search-input"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="Search CJ catalog (e.g. vibrator, lingerie, bdsm)"
                className="pl-9"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <Label htmlFor="category-override" className="text-sm whitespace-nowrap">Category</Label>
                <select
                  id="category-override"
                  data-testid="cj-category-select"
                  value={categoryOverride}
                  onChange={(e) => setCategoryOverride(e.target.value)}
                  className="bg-background border border-border rounded-md px-2 py-2 text-sm"
                >
                  <option value="">Auto-detect</option>
                  {categories.map((c) => (
                    <option key={c.category_id} value={c.category_id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="markup" className="text-sm whitespace-nowrap">Markup €</Label>
                <Input
                  id="markup"
                  data-testid="cj-markup-input"
                  type="number"
                  min="0"
                  step="0.5"
                  value={markup}
                  onChange={(e) => setMarkup(e.target.value)}
                  className="w-24"
                />
              </div>
              <Button
                type="submit"
                data-testid="cj-search-button"
                disabled={loading}
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
          </form>

          <div className="grid gap-3 md:grid-cols-[2fr_1fr_auto] md:items-end" data-testid="cj-browse-controls">
            <div>
              <Label htmlFor="cj-category" className="text-sm">CJ category</Label>
              <select
                id="cj-category"
                data-testid="cj-browse-category"
                value={cjCategory}
                onChange={(e) => setCjCategory(e.target.value)}
                className="mt-1 w-full bg-background border border-border rounded-md px-2 py-2 text-sm"
              >
                <option value="">— Any category (use keyword) —</option>
                {Object.entries(
                  cjCategories
                    .filter((c) => !adultOnly || c.adult)
                    .reduce((acc, c) => ({ ...acc, [c.group]: [...(acc[c.group] || []), c] }), {})
                ).map(([group, cats]) => (
                  <optgroup key={group} label={group}>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </optgroup>
                ))}
              </select>
              {cjCategoriesError && (
                <p className="mt-1 text-xs text-red-500" data-testid="cj-categories-error">{cjCategoriesError}</p>
              )}
              <label className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={adultOnly} onChange={(e) => setAdultOnly(e.target.checked)} />
                Adult categories only
              </label>
            </div>
            <div>
              <Label htmlFor="cj-warehouse" className="text-sm">Ships from</Label>
              <select
                id="cj-warehouse"
                data-testid="cj-browse-warehouse"
                value={warehouse}
                onChange={(e) => setWarehouse(e.target.value)}
                className="mt-1 w-full bg-background border border-border rounded-md px-2 py-2 text-sm"
              >
                <option value="">Any warehouse (incl. China)</option>
                {warehouses.map((w) => <option key={w} value={w}>EU warehouse: {w}</option>)}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">EU stock: 2–6 days, no customs</p>
            </div>
            <Button
              type="button"
              onClick={() => search(keyword, 1)}
              disabled={loading}
              data-testid="cj-browse-button"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Browse'}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p}
                onClick={() => search(p, 1)}
                className="text-xs px-3 py-1 rounded-full border border-border bg-muted hover:bg-accent hover:text-accent-foreground transition"
                data-testid={`cj-preset-${p.replace(/\s+/g, '-')}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {/* Import by specific PID */}
        <div className="bg-card border border-border rounded-xl p-5 space-y-3" data-testid="cj-pid-import">
          <div>
            <h3 className="text-foreground font-semibold mb-1">Import by CJ Product ID or URL</h3>
            <p className="text-xs text-muted-foreground">
              CJ Open API hides explicit adult products from public search. Browse{' '}
              <a
                href="https://www.cjdropshipping.com/list/sex-toys-l-A20-300031.html"
                target="_blank"
                rel="noreferrer"
                className="text-accent underline"
              >
                CJ Adult Catalog
              </a>{' '}
              while logged in, paste a product URL or ID below, and import.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              data-testid="cj-pid-input"
              value={pidInput}
              onChange={(e) => setPidInput(e.target.value)}
              placeholder="e.g. 2046070873585651714 or full CJ URL"
              className="flex-1"
            />
            <Button
              onClick={importByPid}
              disabled={pidImporting}
              data-testid="cj-pid-import-button"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
            >
              {pidImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><Download className="w-4 h-4 mr-2" /> Import this product</>
              )}
            </Button>
          </div>
        </div>

        {/* Results */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="text-center py-20 text-muted-foreground" data-testid="cj-empty-state">
            Search the CJ catalog to see products here.
          </div>
        )}

        {!loading && items.length > 0 && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm text-muted-foreground">
                Showing page {page} of {totalPages} · {total} results
                {keyword ? ` for "${keyword}"` : ''}
                {browsing && warehouse ? ` · in stock in ${warehouse}` : ''}
              </div>
              <Button
                onClick={bulkImport}
                disabled={bulkImporting}
                data-testid="cj-bulk-import-button"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
              >
                {bulkImporting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Import all {items.length} on this page</>
                )}
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {items.map((it) => {
                const retail = (Number(it.sell_price || 0) + Number(markup || 0)).toFixed(2);
                const isImported = imported[it.pid];
                const isLoading = importing[it.pid];
                return (
                  <div
                    key={it.pid}
                    data-testid={`cj-product-${it.pid}`}
                    className="bg-card border border-border rounded-xl overflow-hidden flex flex-col"
                  >
                    <div className="aspect-square bg-muted overflow-hidden">
                      {it.image ? (
                        <img
                          src={it.image}
                          alt={it.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                          No image
                        </div>
                      )}
                    </div>
                    <div className="p-4 flex flex-col flex-1 gap-2">
                      <h3 className="font-medium text-sm line-clamp-2 min-h-[2.5rem]">{it.name}</h3>
                      <div className="text-xs text-muted-foreground line-clamp-1">
                        {it.category_name || 'Uncategorized'}
                        {it.inventory ? ` · ${it.inventory} in stock` : ''}
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xs text-muted-foreground line-through">${it.sell_price.toFixed(2)}</span>
                        <span className="text-lg font-bold text-accent">€{retail}</span>
                      </div>
                      <div className="mt-auto flex gap-2">
                        <a
                          href={`https://www.cjdropshipping.com/product/-p-${it.pid}.html`}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 inline-flex items-center justify-center gap-1 text-xs px-3 py-2 rounded-md border border-border hover:bg-muted transition"
                        >
                          <ExternalLink className="w-3 h-3" /> View
                        </a>
                        <Button
                          size="sm"
                          data-testid={`cj-import-${it.pid}`}
                          disabled={isImported || isLoading}
                          onClick={() => importItem(it.pid)}
                          className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90"
                        >
                          {isImported ? (
                            <><CheckCircle2 className="w-3 h-3 mr-1" /> Imported</>
                          ) : isLoading ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <><Download className="w-3 h-3 mr-1" /> Import</>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 pt-4">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => search(keyword, page - 1)}
                  data-testid="cj-prev-page"
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm">{page} / {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => search(keyword, page + 1)}
                  data-testid="cj-next-page"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCJImport;
